import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/Bits";
import { LogoMark } from "../components/Logo";
import { getPaymentStatus } from "../lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [amount, setAmount] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        setAmount(res.amount);
        if (res.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(res.payment_status)) { setState("failed"); return; }
      } catch { /* keep trying */ }
      attempts.current += 1;
      if (attempts.current > 15) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-nead-ink px-6">
      <div className="max-w-lg text-center text-white">
        <div className="mx-auto mb-8 w-16"><LogoMark className="w-full" /></div>

        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-nead-pink animate-spin" strokeWidth={1.6} />
            <h1 className="mt-6 font-display font-bold text-3xl">Confirming your donation…</h1>
            <p className="mt-4 text-white/60">Just a moment while we check with our payment provider.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-nead-cyan" strokeWidth={1.5} />
            <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl">Thank you!</h1>
            <p className="mt-4 text-white/70 text-lg">
              {amount ? `Your £${amount} donation` : "Your donation"} means a great deal. Every
              penny is reinvested into our work across Cumbria.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Button to="/" variant="pink" testId="success-home">Back home</Button>
              <Button to="/work" variant="outline-light" testId="success-work">See our work</Button>
            </div>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle className="mx-auto h-16 w-16 text-nead-pink" strokeWidth={1.5} />
            <h1 className="mt-6 font-display font-bold text-3xl">Payment not completed</h1>
            <p className="mt-4 text-white/60">Your donation wasn't completed. No charge has been made. Please feel free to try again.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Button to="/donate" variant="pink" testId="failed-retry">Try again</Button>
              <Button to="/" variant="outline-light" testId="failed-home">Back home</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
